import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, ArrowRight, Search, Mail, CheckCircle } from "lucide-react";
import { blogPosts } from "@/data/blogPosts";
import { useSEO } from "@/hooks/useSEO";
import { SITE_CONFIG } from "@/config/site-meta";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";

const POSTS_PER_PAGE = 4;
const categories = Array.from(new Set(blogPosts.map((p) => p.category)));

const Blog = () => {
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [nlEmail, setNlEmail] = useState("");
  const [nlConsent, setNlConsent] = useState(false);
  const [nlLoading, setNlLoading] = useState(false);
  const [nlSuccess, setNlSuccess] = useState(false);

  useSEO({
    title: "Blog de patinetes eléctricos | Guías y normativa",
    description: "Guías de compra, normativa DGT, comparativas y consejos de mantenimiento para patinetes eléctricos en España.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_CONFIG.url },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_CONFIG.url}/blog` },
      ],
    },
  });

  const filtered = useMemo(() => {
    let posts = blogPosts;
    if (activeCategory) posts = posts.filter((p) => p.category === activeCategory);
    if (query.trim()) {
      const q = query.toLowerCase();
      posts = posts.filter(
        (p) => p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q) || p.category.toLowerCase().includes(q)
      );
    }
    return posts;
  }, [query, activeCategory]);

  // Reset page when filters change
  const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE);
  const safeCurrentPage = Math.min(currentPage, totalPages || 1);
  const paginatedPosts = filtered.slice((safeCurrentPage - 1) * POSTS_PER_PAGE, safeCurrentPage * POSTS_PER_PAGE);

  const handleCategoryChange = (cat: string | null) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  const handleSearchChange = (val: string) => {
    setQuery(val);
    setCurrentPage(1);
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nlEmail.trim() || !nlConsent) return;

    setNlLoading(true);
    const { error } = await supabase.from("newsletter_subscribers" as any).insert({
      email: nlEmail.trim().toLowerCase(),
      gdpr_consent: true,
      gdpr_consent_at: new Date().toISOString(),
    } as any);

    setNlLoading(false);
    if (error) {
      if (error.code === "23505") {
        toast.info("Ya estás suscrito al newsletter.");
      } else {
        toast.error("Error al suscribirte. Inténtalo de nuevo.");
      }
      return;
    }
    setNlSuccess(true);
    setNlEmail("");
    setNlConsent(false);
    toast.success("¡Te has suscrito correctamente!");
  };

  return (
    <Layout>
      <section className="py-12">
        <div className="container max-w-5xl">
          <header className="mb-10 text-center">
            <h1 className="font-display text-4xl font-bold">Blog eScooter</h1>
            <p className="mt-3 text-lg text-muted-foreground">
              Guías, normativa, comparativas y consejos sobre patinetes eléctricos en España.
            </p>
          </header>

          {/* Search + Filters */}
          <div className="mb-8 space-y-4">
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar artículos..."
                value={query}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => handleCategoryChange(null)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  !activeCategory ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent"
                }`}
              >
                Todos
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(activeCategory === cat ? null : cat)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    activeCategory === cat ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No se encontraron artículos.</p>
          ) : (
            <>
              <div className="grid gap-8 md:grid-cols-2">
                {paginatedPosts.map((post) => (
                  <Card key={post.slug} className="group overflow-hidden border-border transition-shadow hover:shadow-lg">
                    <Link to={`/blog/${post.slug}`}>
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    </Link>
                    <CardContent className="p-5">
                      <div className="mb-3 flex items-center gap-3">
                        <Badge variant="secondary" className="text-xs">{post.category}</Badge>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarDays className="h-3 w-3" /> {new Date(post.date).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" /> {post.readTime}
                        </span>
                      </div>
                      <Link to={`/blog/${post.slug}`}>
                        <h2 className="font-display text-lg font-semibold leading-snug transition-colors group-hover:text-primary">
                          {post.title}
                        </h2>
                      </Link>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                        {post.excerpt}
                      </p>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                      >
                        Leer más <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination className="mt-10">
                  <PaginationContent>
                    {safeCurrentPage > 1 && (
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => { e.preventDefault(); setCurrentPage(safeCurrentPage - 1); }}
                        />
                      </PaginationItem>
                    )}
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      if (totalPages <= 5 || Math.abs(page - safeCurrentPage) <= 1 || page === 1 || page === totalPages) {
                        return (
                          <PaginationItem key={page}>
                            <PaginationLink
                              href="#"
                              isActive={page === safeCurrentPage}
                              onClick={(e) => { e.preventDefault(); setCurrentPage(page); }}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }
                      if (page === 2 && safeCurrentPage > 3) return <PaginationItem key="start-ellipsis"><PaginationEllipsis /></PaginationItem>;
                      if (page === totalPages - 1 && safeCurrentPage < totalPages - 2) return <PaginationItem key="end-ellipsis"><PaginationEllipsis /></PaginationItem>;
                      return null;
                    })}
                    {safeCurrentPage < totalPages && (
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => { e.preventDefault(); setCurrentPage(safeCurrentPage + 1); }}
                        />
                      </PaginationItem>
                    )}
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}

          {/* Newsletter */}
          <div className="mt-16 rounded-2xl bg-muted/50 border border-border p-8 md:p-12 text-center">
            <Mail className="mx-auto h-10 w-10 text-primary mb-4" />
            <h2 className="font-display text-2xl font-bold">Suscríbete al newsletter</h2>
            <p className="mt-2 text-muted-foreground max-w-md mx-auto">
              Recibe las últimas guías, normativa y ofertas sobre patinetes eléctricos directamente en tu correo.
            </p>

            {nlSuccess ? (
              <div className="mt-6 flex items-center justify-center gap-2 text-primary font-medium">
                <CheckCircle className="h-5 w-5" /> ¡Suscripción confirmada!
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="mt-6 max-w-md mx-auto space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={nlEmail}
                    onChange={(e) => setNlEmail(e.target.value)}
                    required
                    className="flex-1"
                  />
                  <Button type="submit" disabled={nlLoading || !nlConsent}>
                    {nlLoading ? "..." : "Suscribirme"}
                  </Button>
                </div>
                <label className="flex items-start gap-2 text-xs text-muted-foreground text-left cursor-pointer">
                  <input
                    type="checkbox"
                    checked={nlConsent}
                    onChange={(e) => setNlConsent(e.target.checked)}
                    className="mt-0.5 rounded border-input"
                  />
                  Acepto recibir comunicaciones y la política de privacidad (RGPD).
                </label>
              </form>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
